import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Check, ArrowLeft } from 'lucide-react';

const SignUp = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: '',
    phone: '',
    acceptTerms: false,
    acceptRisk: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const countries = [
    'Select Country',
    'United Kingdom',
    'Germany',
    'France',
    'Netherlands',
    'Switzerland',
    'Singapore',
    'Japan',
    'Australia',
    'Canada',
    'India',
    'Brazil',
    'Mexico',
    'South Korea',
    'Hong Kong',
    'UAE',
    'Other'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!formData.country || formData.country === 'Select Country') newErrors.country = 'Please select your country';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms';
    if (!formData.acceptRisk) newErrors.acceptRisk = 'You must acknowledge the risk disclosure';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setSubmitted(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0b0014] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-500/10 flex items-center justify-center">
            <Check className="w-10 h-10 text-purple-400" />
          </div>
          <h1 className="text-3xl font-semibold text-white mb-3">
            Account Under Review
          </h1>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Thank you for signing up! Your account is currently under review. 
            We'll send you an email at <span className="text-white">{formData.email}</span> once your account is approved.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            This usually takes 1-2 business days.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-white bg-[#7B61FF] hover:bg-[#6B4FFF] rounded-lg transition-all"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0014]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0b0014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_stock-mirrors/artifacts/gdgmfuzc_2.%20Logo%202.png" 
                alt="Stocks Protocol" 
                className="h-40 w-auto object-contain"
              />
            </Link>
            
            <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1">
              <ArrowLeft size={16} />
              Back
            </Link>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="pt-28 pb-20">
        <div className="max-w-lg mx-auto px-6">
          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-white mb-2">
              Create Account
            </h1>
            <p className="text-gray-500">
              Start trading tokenized stocks on the blockchain
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-white/[0.03] border ${errors.firstName ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                  placeholder="John"
                />
                {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-white/[0.03] border ${errors.lastName ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                  placeholder="Doe"
                />
                {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white/[0.03] border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                placeholder="john@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Country of Residence</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white/[0.03] border ${errors.country ? 'border-red-500' : 'border-white/10'} rounded-lg text-white focus:outline-none focus:border-purple-500 transition-colors appearance-none cursor-pointer`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5em 1.5em' }}
              >
                {countries.map(country => (
                  <option key={country} value={country} className="bg-[#1a1a2e]">{country}</option>
                ))}
              </select>
              {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Phone Number <span className="text-gray-600">(Optional)</span></label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 pr-12 bg-white/[0.03] border ${errors.password ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                  placeholder="Min. 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 pr-12 bg-white/[0.03] border ${errors.confirmPassword ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Checkboxes */}
            <div className="space-y-4 pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border ${formData.acceptTerms ? 'bg-purple-500 border-purple-500' : 'border-white/20'} flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors`}>
                  {formData.acceptTerms && <Check size={14} className="text-white" />}
                </div>
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                  I agree to the <a href="#" className="text-purple-400 hover:text-purple-300">Terms of Service</a> and <a href="#" className="text-purple-400 hover:text-purple-300">Privacy Policy</a>
                </span>
              </label>
              {errors.acceptTerms && <p className="text-red-400 text-xs ml-8">{errors.acceptTerms}</p>}

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="acceptRisk"
                  checked={formData.acceptRisk}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border ${formData.acceptRisk ? 'bg-purple-500 border-purple-500' : 'border-white/20'} flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors`}>
                  {formData.acceptRisk && <Check size={14} className="text-white" />}
                </div>
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                  I acknowledge that trading involves risk and I have read the <a href="#" className="text-purple-400 hover:text-purple-300">Risk Disclosure</a>
                </span>
              </label>
              {errors.acceptRisk && <p className="text-red-400 text-xs ml-8">{errors.acceptRisk}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-4 mt-6 text-white font-semibold bg-[#7B61FF] hover:bg-[#6B4FFF] rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25"
            >
              Create Account
            </button>

            {/* Sign In Link */}
            <p className="text-center text-gray-500 text-sm pt-4">
              Already have an account?{' '}
              <Link to="/signin" className="text-purple-400 hover:text-purple-300 transition-colors">Sign in</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SignUp;
